'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import Link from 'next/link';

// ---------- types ----------
interface Whale {
  address: string;
  volumeUsd: number;
  tradeCount: number;
  positionValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
  positionCount: number;
  lastActiveAt: string;
  positions: WhalePosition[];
}
interface WhalePosition {
  coin: string; szi: number; entryPx: number; markPx: number; positionValue: number;
  leverage: number; leverageType: string; liquidationPx: number | null;
  unrealizedPnl: number; cumFundingSinceOpen: number;
}
interface Trade { coin: string; px: number; sz: number; side: string; dir: string | null; usdValue: number; isLiquidation: boolean; time: string; taker: string | null; maker: string | null; }
interface Liquidation { coin: string; address: string | null; px: number; sz: number; usdValue: number; side: string; time: string; }
interface OiRow { coin: string; openInterest: number; openInterestUsd: number; markPx: number; time: string; }
interface FundingRow { coin: string; fundingRate: number; premium: number; markPx: number; time: string; }
interface Overview { totalOiUsd: number; totalDayVolume: number; avgFundingRate: number; coinCount: number; whaleCount: number; liquidation1hUsd: number; liquidation24hUsd: number; updatedAt: number; }
interface Transfer {
  id: number; type: string; address: string; coin: string; amountUsd: number;
  txHash: string; blockNumber: string; time: string;
}
interface TransferResp { count: number; transfers: Transfer[]; stats: { deposit24h: number; withdraw24h: number; depositCount24h: number; withdrawCount24h: number }; }
interface WhaleDetail {
  address: string; volumeUsd: number; tradeCount: number; positionValue: number;
  unrealizedPnl: number; realizedPnl: number; realizedPnlFromFills: number; feesPaid: number; fundingNet: number;
  snapshotTime?: string | number;
  positions: Array<{ coin: string; szi: number; side: string; entryPx: number; markPx: number; positionValue: number; leverage: number; leverageType?: string; liquidationPx: number | null; unrealizedPnl: number; cumFundingSinceOpen: number }>;
  recentFills: Array<{ coin: string; px: number; sz: number; side: string; dir: string; closedPnl: number; fee: number; usdValue: number; isLiquidation: boolean; time: string }>;
  recentFundings: Array<{ coin: string; usdc: number; szi: number; fundingRate: number; time: string }>;
}

const fmtUsd = (n?: number | null, compact = false): string => {
  if (n === undefined || n === null || !isFinite(n)) return '—';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(n);
  if (compact && abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (compact && abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (compact && abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: abs < 10 ? 4 : 2 })}`;
};
const fmtNum = (n: number, d = 2): string => {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: d });
};
/** D21: 小额标记价遵循有效精度——<0.0001 用 8 位、<0.01 用 6 位、其余按值保留 4 位。 */
const fmtPx = (n: number): string => {
  if (!isFinite(n)) return '—';
  const abs = Math.abs(n);
  const d = abs < 0.0001 ? 8 : abs < 0.01 ? 6 : 4;
  return n.toLocaleString(undefined, { maximumFractionDigits: d });
};
const fmtTime = (ts: string | number, lang: string): string => {
  const t = Number(ts);
  if (!t) return '—';
  const d = new Date(t);
  const pad = (x: number) => String(x).padStart(2, '0');
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  if (Date.now() - t < 86400_000) return hm;
  return `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
};
const shortAddr = (a: string): string => `${a.slice(0, 6)}…${a.slice(-4)}`;
const pnlColor = (n: number): string => (n >= 0 ? 'text-emerald-400' : 'text-red-400');
const pnlSign = (n: number): string => (n > 0 ? '+' : '');

type WhaleSort = 'positionValue' | 'unrealizedPnl' | 'realizedPnl' | 'volumeUsd';

export default function HyperliquidPage() {
  const { language } = useAppStore();
  const authReady = useRequireAuth('/hyperliquid', true);
  const zh = language !== 'en';
  const [sort, setSort] = useState<WhaleSort>('positionValue');
  const [selected, setSelected] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [tab, setTab] = useState<'whales' | 'trades' | 'liquidations' | 'oi' | 'funding' | 'transfers'>('whales');
  // D21: large trades / liquidations filters — coin keyword, direction, min amount, time window
  const [tradeCoin, setTradeCoin] = useState('');
  const [tradeSide, setTradeSide] = useState('all');
  const [tradeMinUsd, setTradeMinUsd] = useState(0);
  const [tradeH, setTradeH] = useState(0); // 0 = no time filter
  const [liqCoin, setLiqCoin] = useState('');
  const [liqSide, setLiqSide] = useState('all');
  const [liqMinUsd, setLiqMinUsd] = useState(0);
  const [liqH, setLiqH] = useState(0);
  const [expandedTx, setExpandedTx] = useState<string | null>(null); // mobile row expand: 't-i' | 'l-i' | 'x-i'

  const overviewQuery = useQuery({
    queryKey: ['hl-overview'],
    queryFn: () => api.get<Overview>('/hyperliquid/overview'),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });
  const { data: overview, isError: overviewError, dataUpdatedAt, refetch: refetchOverview } = overviewQuery;
  const { data: whales, isLoading: whalesLoading, isError: whalesError } = useQuery({
    queryKey: ['hl-whales', sort],
    queryFn: () => api.get<{ count: number; whales: Whale[] }>(`/hyperliquid/whales?sort=${sort}&order=desc&limit=50`),
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
  const { data: trades, isLoading: tradesLoading, isError: tradesError } = useQuery({
    queryKey: ['hl-trades', tradeCoin, tradeSide, tradeMinUsd, tradeH],
    queryFn: () => {
      const p = new URLSearchParams({ limit: '50' });
      if (tradeCoin.trim()) p.set('coin', tradeCoin.trim().toUpperCase());
      if (tradeSide !== 'all') p.set('side', tradeSide);
      if (tradeMinUsd > 0) p.set('minUsd', String(tradeMinUsd));
      if (tradeH > 0) p.set('since', String(Date.now() - tradeH * 3600_000));
      return api.get<{ count: number; trades: Trade[]; thresholdUsd?: number }>(`/hyperliquid/trades?${p.toString()}`);
    },
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const { data: liquidations, isLoading: liqsLoading, isError: liqsError } = useQuery({
    queryKey: ['hl-liquidations', liqCoin, liqSide, liqMinUsd, liqH],
    queryFn: () => {
      const p = new URLSearchParams({ limit: '50' });
      if (liqCoin.trim()) p.set('coin', liqCoin.trim().toUpperCase());
      if (liqSide !== 'all') p.set('side', liqSide);
      if (liqMinUsd > 0) p.set('minUsd', String(liqMinUsd));
      if (liqH > 0) p.set('since', String(Date.now() - liqH * 3600_000));
      return api.get<{ count: number; liquidations: Liquidation[]; thresholdUsd?: number }>(`/hyperliquid/liquidations?${p.toString()}`);
    },
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const { data: oi, isLoading: oiLoading, isError: oiError } = useQuery({
    queryKey: ['hl-oi'],
    queryFn: () => api.get<{ count: number; oi: OiRow[] }>('/hyperliquid/oi?limit=50'),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });
  const { data: funding, isLoading: fundingLoading, isError: fundingError } = useQuery({
    queryKey: ['hl-funding'],
    queryFn: () => api.get<{ count: number; funding: FundingRow[] }>('/hyperliquid/funding?limit=50'),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });
  const { data: detail } = useQuery({
    queryKey: ['hl-whale-detail', selected],
    queryFn: () => api.get<WhaleDetail>(`/hyperliquid/whales/${selected}`),
    enabled: !!selected,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const { data: transfers, isLoading: transfersLoading, isError: transfersError } = useQuery({
    queryKey: ['hl-transfers'],
    queryFn: () => api.get<TransferResp>('/hyperliquid/transfers?limit=50'),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });

  // AUD17: shared loading / error / empty states for the six tabs
  const stateNote = (loading: boolean, error: boolean, emptyZh: string, emptyEn: string) => {
    if (loading) return zh ? '加载中…' : 'Loading…';
    if (error) return zh ? '获取失败，请稍后重试' : 'Failed to load, please retry';
    return zh ? emptyZh : emptyEn;
  };

  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: 'whales', label: zh ? '大户持仓' : 'Whales' },
    { key: 'trades', label: zh ? '大额成交 ≥$50K' : 'Large Trades' },
    { key: 'liquidations', label: zh ? '大额清算 ≥$1K' : 'Large Liqs' },
    { key: 'transfers', label: zh ? '转账监控' : 'Whale Transfers' },
    { key: 'oi', label: zh ? 'HL 持仓' : 'HL OI' },
    { key: 'funding', label: zh ? '资金费率' : 'Funding' },
  ];

  
  return (
    <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6">
      <EventPing event="chain_view" />
      {/* header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold sm:text-2xl">{zh ? 'Hyperliquid 链上监控' : 'Hyperliquid On-chain Monitor'}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            href="/cross-assets"
            className="flex items-center gap-1.5 rounded-lg border border-gf-border bg-gf-surface px-2.5 py-1 text-xs text-gf-text transition-colors hover:border-gf-accent/50 hover:text-gf-accent"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 12h4l2-7 4 14 2-7h6" />
            </svg>
            {zh ? '跨资产对比' : 'Cross-Assets'}
          </Link>
          <div className="text-xs text-gf-muted">
            {zh ? '30-90s 自动刷新' : 'auto-refresh 30-90s'}
          </div>
        </div>
      </div>

      {/* AUD17: stale / error banner with local retry */}
      {overviewError && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gf-warn/30 bg-gf-warn/10 px-3 py-2 text-xs text-gf-warn">
          <span>
            {zh
              ? `数据延迟或获取失败，显示为最后一次成功结果${dataUpdatedAt ? `（${new Date(dataUpdatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}）` : ''}`
              : `Data delayed or failed; showing last good snapshot${dataUpdatedAt ? ` (${new Date(dataUpdatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })})` : ''}`}
          </span>
          <button
            type="button"
            onClick={() => refetchOverview()}
            className="rounded-md border border-gf-warn/40 px-2 py-1 text-gf-warn hover:bg-gf-warn/10"
          >
            {zh ? '重试' : 'Retry'}
          </button>
        </div>
      )}

      {/* overview cards: mobile shows 4 core summaries + expand, desktop shows all 6 */}
      <div className="mb-1 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <OverviewCard label={zh ? 'HL 全市场持仓' : 'HL Total OI'} value={fmtUsd(overview?.totalOiUsd, true)} loading={!overview && !overviewError} />
        <OverviewCard label={zh ? '24h 成交额' : '24h Volume'} value={fmtUsd(overview?.totalDayVolume, true)} loading={!overview && !overviewError} />
        <OverviewCard label={zh ? '资金费率均值' : 'Avg Funding'} value={overview ? `${(overview.avgFundingRate * 100).toFixed(4)}%` : '—'} accent loading={!overview && !overviewError} />
        <OverviewCard label={zh ? '24h 清算' : '24h Liq.'} value={fmtUsd(overview?.liquidation24hUsd, true)} loading={!overview && !overviewError} />
        <OverviewCard cls="hidden sm:block" label={zh ? '1h 清算' : '1h Liq.'} value={fmtUsd(overview?.liquidation1hUsd, true)} loading={!overview && !overviewError} />
        <OverviewCard cls="hidden sm:block" label={zh ? '追踪大户' : 'Whales'} value={overview ? `${overview.whaleCount}` : '—'} loading={!overview && !overviewError} />
      </div>
      <p className="mb-2 text-[10px] leading-relaxed text-gf-muted">
        {zh
          ? '注：持仓与资金费率数据仅覆盖 Hyperliquid 单一交易所；「追踪大户」为按持仓/成交识别的监控地址数，不等于全链地址数。'
          : 'Note: OI & funding data cover Hyperliquid only; "Tracked whales" is the count of monitored addresses identified by position/volume, not all on-chain addresses.'}
      </p>
      {!more ? (
        <button
          onClick={() => setMore(true)}
          className="mb-4 flex w-full items-center justify-center gap-1 rounded-xl border border-dashed border-gf-border bg-gf-card/50 py-2.5 text-xs text-gf-muted transition-colors hover:border-gf-accent/40 hover:text-gf-text sm:hidden"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="m6 9 6 6 6-6" />
          </svg>
          {zh ? '展开 2 项 · 1H 清算 / 追踪大户' : 'Show 2 more · 1h Liq / Whales'}
        </button>
      ) : (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:hidden">
          <OverviewCard label={zh ? '1h 清算' : '1h Liq.'} value={fmtUsd(overview?.liquidation1hUsd, true)} loading={!overview && !overviewError} />
          <OverviewCard label={zh ? '追踪大户' : 'Whales'} value={overview ? `${overview.whaleCount}` : '—'} loading={!overview && !overviewError} />
        </div>
      )}

      {/* AUD17: single-row horizontally scrollable tabs on mobile */}
      <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === t.key
                ? 'bg-gf-accent text-white'
                : 'border border-gf-border text-gf-muted hover:text-gf-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ============ WHALES ============ */}
      {tab === 'whales' && (
        <div>
          {/* AUD17: sort — select on mobile, buttons on desktop */}
          <div className="mb-2 flex items-center gap-2 text-xs text-gf-muted">
            <span className="shrink-0">{zh ? '排序：' : 'Sort: '}</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as WhaleSort)}
              aria-label={zh ? '排序方式' : 'Sort by'}
              className="input !h-8 !w-auto !py-0 text-xs sm:hidden"
            >
              {([
                ['positionValue', zh ? '持仓价值' : 'Position'],
                ['unrealizedPnl', zh ? '未实现盈亏' : 'Unrealized PnL'],
                ['realizedPnl', zh ? '已实现盈亏' : 'Realized PnL'],
                ['volumeUsd', zh ? '成交额' : 'Volume'],
              ] as Array<[WhaleSort, string]>).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
            <div className="hidden flex-wrap gap-1 sm:flex">
              {([
                ['positionValue', zh ? '持仓价值' : 'Position'],
                ['unrealizedPnl', zh ? '未实现盈亏' : 'Unrealized PnL'],
                ['realizedPnl', zh ? '已实现盈亏' : 'Realized PnL'],
                ['volumeUsd', zh ? '成交额' : 'Volume'],
              ] as Array<[WhaleSort, string]>).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setSort(k)}
                  className={`rounded-md px-2 py-1 ${sort === k ? 'bg-gf-accent/15 text-gf-accent' : 'hover:bg-white/5'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* whale list - responsive cards on mobile, table on desktop */}
          {/* AUD17: loading / error / empty states */}
          {(!whales?.whales || !whales.whales.length) && (
            <div className="px-3 py-8 text-center text-xs text-gf-muted">
              {stateNote(whalesLoading, whalesError, '暂无大户数据，链上数据拉取中…', 'No whale data yet, fetching on-chain data…')}
            </div>
          )}
          <div className="space-y-2">
            {whales?.whales.map((w, idx) => (
              <div
                key={w.address}
                className={`cursor-pointer rounded-xl border bg-gf-card p-3 transition-colors hover:border-gf-accent/40 ${
                  selected === w.address ? 'border-gf-accent/60' : 'border-gf-border'
                }`}
                onClick={() => setSelected(selected === w.address ? null : w.address)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="w-5 shrink-0 text-center text-xs text-gf-muted">{idx + 1}</span>
                    <span className="font-mono text-sm text-gf-text">{shortAddr(w.address)}</span>
                    <span className="shrink-0 rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-gf-muted">
                      {w.positionCount} {zh ? '仓位' : 'pos'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-right text-xs">
                      <span className={pnlColor(w.unrealizedPnl)}>
                        {pnlSign(w.unrealizedPnl)}{fmtUsd(w.unrealizedPnl)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelected(selected === w.address ? null : w.address); }}
                      className={`shrink-0 rounded-md border px-2 py-1 text-[10px] transition-colors ${
                        selected === w.address
                          ? 'border-gf-accent/60 bg-gf-accent/15 text-gf-accent'
                          : 'border-gf-border text-gf-muted hover:border-gf-accent/50 hover:text-gf-accent'
                      }`}
                    >
                      {zh ? '查看仓位' : 'Positions'}
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 grid grid-cols-3 gap-2 text-xs sm:grid-cols-5">
                  <MiniStat label={zh ? '持仓价值' : 'Position'} value={fmtUsd(w.positionValue, true)} />
                  <MiniStat label={zh ? '未实现' : 'uPnL'} value={fmtUsd(w.unrealizedPnl, true)} cls={pnlColor(w.unrealizedPnl)} />
                  <MiniStat label={zh ? '已实现' : 'rPnL'} value={fmtUsd(w.realizedPnl, true)} cls={pnlColor(w.realizedPnl)} title={zh ? '已实现盈亏为自最后一次清仓起累计，非账户终身表现' : 'Realized PnL accumulates since the last flat position; not lifetime performance'} />
                  <MiniStat label={zh ? '成交额' : 'Volume'} value={fmtUsd(w.volumeUsd, true)} title={zh ? '为监控窗口内成交额，非历史总成交' : 'Volume within the monitored window, not all-time'} />
                  <MiniStat label={zh ? '交易数' : 'Trades'} value={String(w.tradeCount)} title={zh ? '为监控窗口内交易数' : 'Trades within the monitored window'} />
                </div>
                {/* expandable detail */}
                {selected === w.address && detail && (
                  <div className="mt-3 border-t border-gf-border pt-3">
                    {/* AUD17: full address — copy + trusted explorer link */}
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-gf-text">{detail.address}</span>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(detail.address)}
                        className="rounded-md border border-gf-border px-2 py-0.5 text-[10px] text-gf-muted hover:text-gf-text"
                      >
                        {zh ? '复制' : 'Copy'}
                      </button>
                      <a
                        href={`https://app.hyperliquid.xyz/explorer/address/${detail.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-md border border-gf-border px-2 py-0.5 text-[10px] text-gf-accent hover:border-gf-accent/50"
                      >
                        {zh ? '链上浏览器 ↗' : 'Explorer ↗'}
                      </a>
                      <span className="text-[10px] text-gf-muted">
                        {zh ? '快照时间' : 'Snapshot'}: {fmtTime(detail.snapshotTime ?? detail.recentFills[0]?.time ?? Date.now(), language)}
                      </span>
                    </div>
                    <div className="mb-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                      <StatBox label={zh ? '已实现盈亏' : 'Realized PnL'} value={fmtUsd(detail.realizedPnl)} cls={pnlColor(detail.realizedPnl)} />
                      <StatBox label={zh ? '已实现(成交)' : 'From Fills'} value={fmtUsd(detail.realizedPnlFromFills)} cls={pnlColor(detail.realizedPnlFromFills)} />
                      <StatBox label={zh ? '手续费' : 'Fees'} value={fmtUsd(detail.feesPaid)} />
                      <StatBox label={zh ? '资金费净额' : 'Funding Net'} value={fmtUsd(detail.fundingNet)} cls={pnlColor(detail.fundingNet)} />
                    </div>
                    <div className="mb-2 text-[10px] text-gf-muted">
                      {zh
                        ? '已实现盈亏 = 平仓成交盈亏 + 资金费流水 + 手续费（自最后一次清仓起累计，与列表中即时 rPnL 为不同时间范围）'
                        : 'Realized PnL = closed-fill PnL + funding flows + fees, accumulated since last flat; it is a different window than the live rPnL in the list.'}
                    </div>

                    <div className="mb-1.5 text-[11px] font-medium text-gf-muted">{zh ? '当前持仓' : 'Open Positions'}</div>
                    <div className="space-y-1.5">
                      {detail.positions.map((p) => (
                        <div key={p.coin} className="rounded-lg bg-white/[0.03] px-2.5 py-2 text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="font-medium">{p.coin}</span>
                              <span className={`rounded px-1 py-0.5 text-[10px] ${p.side === 'long' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                                {zh ? (p.side === 'long' ? '多' : '空') : p.side}
                              </span>
                            </div>
                            <span className={pnlColor(p.unrealizedPnl)}>
                              <span className="text-[10px] text-gf-muted">{zh ? '盈亏 ' : 'PnL '}</span>
                              {pnlSign(p.unrealizedPnl)}{fmtUsd(p.unrealizedPnl)}
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
                            <span className="min-w-0">
                              <span className="mr-1 text-[10px] text-gf-muted">{zh ? '数量' : 'Size'}</span>
                              <span>{fmtNum(p.szi)}{p.szi && !p.coin.includes('USDC') ? ` ${p.coin}` : ''}</span>
                            </span>
                            <span className="min-w-0">
                              <span className="mr-1 text-[10px] text-gf-muted">{zh ? '开仓价' : 'Entry'}</span>
                              <span>${fmtPx(p.entryPx)}</span>
                            </span>
                            <span className="min-w-0">
                              <span className="mr-1 text-[10px] text-gf-muted">{zh ? '杠杆/保证金' : 'Lev/Margin'}</span>
                              <span>{fmtNum(p.leverage, 0)}x · {p.leverageType === 'isolated' ? (zh ? '逐仓' : 'Isolated') : p.leverageType === 'cross' ? (zh ? '全仓' : 'Cross') : (zh ? '—' : '—')}</span>
                            </span>
                            <span className="min-w-0">
                              <span className="mr-1 text-[10px] text-gf-muted">{zh ? '标记价' : 'Mark'}</span>
                              <span>${fmtPx(p.markPx)}</span>
                            </span>
                          </div>
                          {p.liquidationPx != null && (
                            <div className="mt-1.5 text-[11px] text-gf-muted">
                              {zh ? '强平价 ' : 'Liq. '}<span className="font-mono">${fmtPx(p.liquidationPx)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {detail.recentFills.length > 0 && (
                      <>
                        <div className="mb-1.5 mt-3 text-[11px] font-medium text-gf-muted">{zh ? '最近成交' : 'Recent Fills'}</div>
                        <div className="overflow-x-auto rounded-lg border border-gf-border">
                          <table className="w-full min-w-[620px] text-xs">
                            <thead>
                              <tr className="border-b border-gf-border bg-white/[0.03] text-left text-[10px] font-medium text-gf-muted">
                                <th className="px-2 py-1.5">{zh ? '时间' : 'Time'}</th>
                                <th className="px-2 py-1.5">{zh ? '币种' : 'Coin'}</th>
                                <th className="px-2 py-1.5">{zh ? '方向' : 'Dir'}</th>
                                <th className="px-2 py-1.5 text-right">{zh ? '价格' : 'Price'}</th>
                                <th className="px-2 py-1.5 text-right">{zh ? '数量' : 'Size'}</th>
                                <th className="px-2 py-1.5 text-right">{zh ? '金额' : 'Value'}</th>
                                <th className="px-2 py-1.5 text-right">{zh ? '盈亏' : 'PnL'}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.03]">
                              {detail.recentFills.slice(0, 12).map((f, i) => (
                                <tr key={i} className="hover:bg-white/[0.02]">
                                  <td className="whitespace-nowrap px-2 py-1.5 text-gf-muted">{fmtTime(f.time, language)}</td>
                                  <td className="px-2 py-1.5 font-medium">{f.coin}</td>
                                  <td className="px-2 py-1.5">
                                    <DirTag dir={f.dir} side={f.side} zh={zh} isLiq={f.isLiquidation} />
                                  </td>
                                  <td className="px-2 py-1.5 text-right">{fmtNum(f.px, 4)}</td>
                                  <td className="px-2 py-1.5 text-right">{fmtNum(f.sz)}</td>
                                  <td className="px-2 py-1.5 text-right">{fmtUsd(f.usdValue)}</td>
                                  <td className={`px-2 py-1.5 text-right ${pnlColor(f.closedPnl)}`}>
                                    {f.closedPnl !== 0 ? `${pnlSign(f.closedPnl)}${fmtUsd(f.closedPnl)}` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            {!whales?.whales.length && (
              <div className="rounded-xl border border-gf-border py-10 text-center text-sm text-gf-muted">
                {zh ? '正在从链上识别大户，请稍候…' : 'Discovering whales from on-chain data…'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ TRADES ============ */}
      {tab === 'trades' && (
        <div>
          <FilterBar
            zh={zh}
            coin={tradeCoin} setCoin={setTradeCoin}
            side={tradeSide} setSide={setTradeSide}
            minUsd={tradeMinUsd} setMinUsd={setTradeMinUsd}
            hours={tradeH} setHours={setTradeH}
            amountOptions={[0, 50_000, 100_000, 500_000, 1_000_000]}
            sideOptions={[['all', zh ? '全部' : 'All'], ['buy', zh ? '买' : 'Buy'], ['sell', zh ? '卖' : 'Sell']]}
            thresholdNote={zh ? '列表含 ≥$50K 单笔成交（大额）' : 'List includes single fills ≥$50K (large)'}
          />
          {/* mobile cards: coin / direction / amount first, full address in detail */}
          <div className="space-y-2 sm:hidden">
            {trades?.trades.map((t, i) => {
              const key = `t-${i}`;
              const open = expandedTx === key;
              return (
                <div key={key} className="rounded-xl border border-gf-border bg-gf-card">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                    onClick={() => setExpandedTx(open ? null : key)}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{t.coin}</span>
                        <DirTag dir={t.dir || ''} side={t.side} zh={zh} isLiq={t.isLiquidation} />
                        {t.usdValue >= 1e6 && <span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] text-amber-400">大</span>}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gf-muted">{fmtTime(t.time, language)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold tabular-nums">{fmtUsd(t.usdValue)}</div>
                      <div className="text-[11px] text-gf-muted">@{fmtPx(t.px)}</div>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-gf-border px-3 py-2 text-xs text-gf-muted">
                      <div className="flex justify-between py-0.5"><span>{zh ? '数量' : 'Size'}</span><span>{fmtNum(t.sz)} {t.coin}</span></div>
                      <div className="flex justify-between py-0.5"><span>{zh ? '价格' : 'Price'}</span><span>${fmtPx(t.px)}</span></div>
                      <div className="flex justify-between gap-2 py-0.5">
                        <span>{zh ? '地址' : 'Address'}</span>
                        <span className="break-all font-mono text-right">
                          {(t.taker || t.maker) ? (
                            <a href={`https://app.hyperliquid.xyz/explorer/address/${t.taker || t.maker}`} target="_blank" rel="noreferrer" className="text-gf-accent">{t.taker || t.maker}</a>
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {(!trades?.trades || !trades.trades.length) && (
              <div className="rounded-xl border border-gf-border px-3 py-6 text-center text-xs text-gf-muted">
                {stateNote(tradesLoading, tradesError, '暂无大额成交数据，等待链上大单…', 'No large trades yet, waiting for on-chain activity…')}
              </div>
            )}
          </div>
          {/* desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-gf-border sm:block">
            <table className="w-full min-w-[720px] text-xs">
              <thead>
                <tr className="border-b border-gf-border bg-white/[0.03] text-left text-[11px] font-medium text-gf-muted">
                  <th className="px-3 py-2">{zh ? '时间' : 'Time'}</th>
                  <th className="px-3 py-2">{zh ? '币种' : 'Coin'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '价格' : 'Price'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '数量' : 'Size'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '金额' : 'Value'}</th>
                  <th className="px-3 py-2">{zh ? '方向' : 'Side'}</th>
                  <th className="px-3 py-2">{zh ? '地址' : 'Address'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                {trades?.trades.map((t, i) => (
                  <tr key={i} className="hover:bg-white/[0.02]">
                    <td className="px-3 py-2 text-gf-muted">{fmtTime(t.time, language)}</td>
                    <td className="px-3 py-2 font-medium">{t.coin}</td>
                    <td className="px-3 py-2 text-right">{fmtPx(t.px)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(t.sz)}</td>
                    <td className={`px-3 py-2 text-right ${t.usdValue >= 1e6 ? 'text-amber-400' : ''}`}>{fmtUsd(t.usdValue)}</td>
                    <td className="px-3 py-2">
                      <DirTag dir={t.dir || ''} side={t.side} zh={zh} isLiq={t.isLiquidation} />
                    </td>
                    <td className="px-3 py-2">
                      {(t.taker || t.maker) ? (
                        <a
                          href={`https://app.hyperliquid.xyz/explorer/address/${t.taker || t.maker}`}
                          target="_blank"
                          rel="noreferrer"
                          title={t.taker || t.maker || ''}
                          className="font-mono text-gf-muted hover:text-gf-accent"
                        >
                          {shortAddr(t.taker || t.maker || '')}
                        </a>
                      ) : (
                        <span className="text-gf-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!trades?.trades || !trades.trades.length) && (
              <div className="px-3 py-8 text-center text-xs text-gf-muted">
                {stateNote(tradesLoading, tradesError, '暂无大额成交数据，等待链上大单…', 'No large trades yet, waiting for on-chain activity…')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ LIQUIDATIONS ============ */}
      {tab === 'liquidations' && (
        <div>
          <FilterBar
            zh={zh}
            coin={liqCoin} setCoin={setLiqCoin}
            side={liqSide} setSide={setLiqSide}
            minUsd={liqMinUsd} setMinUsd={setLiqMinUsd}
            hours={liqH} setHours={setLiqH}
            amountOptions={[0, 1_000, 5_000, 50_000, 100_000]}
            sideOptions={[['all', zh ? '全部' : 'All'], ['long', zh ? '多单爆仓' : 'Long'], ['short', zh ? '空单爆仓' : 'Short']]}
            thresholdNote={zh ? '列表含 ≥$1K 清算（大额）' : 'List includes liquidations ≥$1K (large)'}
          />
          {/* mobile cards */}
          <div className="space-y-2 sm:hidden">
            {liquidations?.liquidations.map((l, i) => {
              const key = `l-${i}`;
              const open = expandedTx === key;
              return (
                <div key={key} className="rounded-xl border border-gf-border bg-gf-card">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                    onClick={() => setExpandedTx(open ? null : key)}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{l.coin}</span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] ${l.side === 'long' ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                          {zh ? (l.side === 'long' ? '多单爆仓' : '空单爆仓') : l.side === 'long' ? 'Long LIQ' : 'Short LIQ'}
                        </span>
                        {l.usdValue >= 1e6 && <span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] text-amber-400">大</span>}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gf-muted">{fmtTime(l.time, language)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold tabular-nums">{fmtUsd(l.usdValue)}</div>
                      <div className="text-[11px] text-gf-muted">@{fmtPx(l.px)}</div>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-gf-border px-3 py-2 text-xs text-gf-muted">
                      <div className="flex justify-between py-0.5"><span>{zh ? '数量' : 'Size'}</span><span>{fmtNum(l.sz)} {l.coin}</span></div>
                      <div className="flex justify-between py-0.5"><span>{zh ? '价格' : 'Price'}</span><span>${fmtPx(l.px)}</span></div>
                      <div className="flex justify-between gap-2 py-0.5">
                        <span>{zh ? '地址' : 'Address'}</span>
                        <span className="break-all font-mono text-right">
                          {l.address ? (
                            <a href={`https://app.hyperliquid.xyz/explorer/address/${l.address}`} target="_blank" rel="noreferrer" className="text-gf-accent">{l.address}</a>
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {(!liquidations?.liquidations || !liquidations.liquidations.length) && (
              <div className="rounded-xl border border-gf-border px-3 py-6 text-center text-xs text-gf-muted">
                {stateNote(liqsLoading, liqsError, '暂无大额清算数据，链上强平发生时自动实时记录', 'No large liquidations yet, will record automatically on-chain')}
              </div>
            )}
          </div>
          {/* desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-gf-border sm:block">
            <table className="w-full min-w-[720px] text-xs">
              <thead>
                <tr className="border-b border-gf-border bg-white/[0.03] text-left text-[11px] font-medium text-gf-muted">
                  <th className="px-3 py-2">{zh ? '时间' : 'Time'}</th>
                  <th className="px-3 py-2">{zh ? '币种' : 'Coin'}</th>
                  <th className="px-3 py-2">{zh ? '方向' : 'Side'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '价格' : 'Price'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '数量' : 'Size'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '金额' : 'Value'}</th>
                  <th className="px-3 py-2">{zh ? '地址' : 'Address'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                {liquidations?.liquidations.map((l, i) => (
                  <tr key={i} className="hover:bg-white/[0.02]">
                    <td className="px-3 py-2 text-gf-muted">{fmtTime(l.time, language)}</td>
                    <td className="px-3 py-2 font-medium">{l.coin}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] ${l.side === 'long' ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                        {zh ? (l.side === 'long' ? '多单爆仓' : '空单爆仓') : l.side === 'long' ? 'Long LIQ' : 'Short LIQ'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{fmtPx(l.px)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(l.sz)}</td>
                    <td className={`px-3 py-2 text-right ${l.usdValue >= 1e6 ? 'text-amber-400' : ''}`}>{fmtUsd(l.usdValue)}</td>
                    <td className="px-3 py-2">
                      {l.address ? (
                        <a
                          href={`https://app.hyperliquid.xyz/explorer/address/${l.address}`}
                          target="_blank"
                          rel="noreferrer"
                          title={l.address}
                          className="font-mono text-gf-muted hover:text-gf-accent"
                        >
                          {shortAddr(l.address)}
                        </a>
                      ) : (
                        <span className="text-gf-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!liquidations?.liquidations || !liquidations.liquidations.length) && (
              <div className="px-3 py-8 text-center text-xs text-gf-muted">
                {stateNote(liqsLoading, liqsError, '暂无大额清算数据，链上强平发生时自动实时记录', 'No large liquidations yet, will record automatically on-chain')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ TRANSFERS (bridge whale monitor) ============ */}
      {tab === 'transfers' && (
        <div>
          {/* 24h stats — D21: 已观测 vs 完整净流量 */}
          <div className="mb-3 grid grid-cols-3 gap-2">
            <OverviewCard label={zh ? '24h 充值 (已观测)' : '24h Dep (observed)'} value={fmtUsd(transfers?.stats.deposit24h ?? 0, true)} accent />
            <OverviewCard label={zh ? '24h 提现 (已观测)' : '24h With (observed)'} value={fmtUsd(transfers?.stats.withdraw24h ?? 0, true)} />
            <OverviewCard
              label={zh ? '24h 已观测净流入' : '24h Observed Net'}
              value={fmtUsd((transfers?.stats.deposit24h ?? 0) - (transfers?.stats.withdraw24h ?? 0), true)}
              cls={(transfers?.stats.deposit24h ?? 0) - (transfers?.stats.withdraw24h ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
            />
          </div>
          <p className="mb-3 rounded-lg border border-gf-warn/30 bg-gf-warn/10 px-3 py-2 text-[10px] leading-relaxed text-gf-warn">
            {zh
              ? '已观测净流量：仅统计 Hyperliquid Bridge2 桥上已观测的充值/提现事件（24h 窗口）。充值数据缺失时不会按 $0 计为真实净流出，此数不等于全链完整净流量。'
              : 'Observed net flow: only bridge events observed on Hyperliquid Bridge2 (24h window). Missing deposits are never treated as $0 in net outflow; this is not the full-chain net flow.'}
          </p>

          {/* mobile cards */}
          <div className="space-y-2 sm:hidden">
            {transfers?.transfers.map((t, i) => {
              const key = `x-${i}`;
              const open = expandedTx === key;
              return (
                <div key={t.id ?? key} className="rounded-xl border border-gf-border bg-gf-card">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                    onClick={() => setExpandedTx(open ? null : key)}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] ${t.type === 'deposit' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                          {zh ? (t.type === 'deposit' ? '充值' : '提现') : t.type === 'deposit' ? 'Deposit' : 'Withdraw'}
                        </span>
                        <span className="text-sm font-semibold">{t.coin}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-gf-muted">{fmtTime(t.time, language)}</div>
                    </div>
                    <div className={`text-sm font-bold tabular-nums ${t.type === 'deposit' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {t.type === 'deposit' ? '+' : '−'}{fmtUsd(t.amountUsd)}
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-gf-border px-3 py-2 text-xs text-gf-muted">
                      <div className="flex justify-between gap-2 py-0.5">
                        <span>{zh ? '地址' : 'Address'}</span>
                        <a href={`https://app.hyperliquid.xyz/explorer/address/${t.address}`} target="_blank" rel="noreferrer" className="break-all font-mono text-right text-gf-accent">{t.address}</a>
                      </div>
                      <div className="flex justify-between gap-2 py-0.5">
                        <span>{zh ? '交易哈希' : 'Tx Hash'}</span>
                        <a href={`https://arbiscan.io/tx/${t.txHash}`} target="_blank" rel="noreferrer" className="break-all font-mono text-right text-gf-accent">{t.txHash}</a>
                      </div>
                      <div className="flex justify-between py-0.5"><span>{zh ? '金额' : 'Amount'}</span><span>{fmtUsd(t.amountUsd)}</span></div>
                    </div>
                  )}
                </div>
              );
            })}
            {(!transfers?.transfers || !transfers.transfers.length) && (
              <div className="rounded-xl border border-gf-border px-3 py-6 text-center text-xs text-gf-muted">
                {stateNote(transfersLoading, transfersError, '暂无大额转账（≥$50k），桥上发生充值/提现时自动实时记录', 'No large transfers (≥$50k) yet — deposits/withdrawals crossing the bridge will appear automatically')}
              </div>
            )}
          </div>

          {/* desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-gf-border sm:block">
            <table className="w-full min-w-[720px] text-xs">
              <thead>
                <tr className="border-b border-gf-border bg-white/[0.03] text-left text-[11px] font-medium text-gf-muted">
                  <th className="px-3 py-2">{zh ? '时间' : 'Time'}</th>
                  <th className="px-3 py-2">{zh ? '方向' : 'Type'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '金额' : 'Amount'}</th>
                  <th className="px-3 py-2">{zh ? '地址' : 'Address'}</th>
                  <th className="px-3 py-2">{zh ? '交易哈希' : 'Tx Hash'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                {transfers?.transfers.map((t, i) => (
                  <tr key={t.id || i} className="hover:bg-white/[0.02]">
                    <td className="whitespace-nowrap px-3 py-2 text-gf-muted">{fmtTime(t.time, language)}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] ${t.type === 'deposit' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {zh ? (t.type === 'deposit' ? '充值' : '提现') : t.type === 'deposit' ? 'Deposit' : 'Withdraw'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={t.type === 'deposit' ? 'text-emerald-400' : 'text-red-400'}>{fmtUsd(t.amountUsd)}</span>
                    </td>
                    <td className="px-3 py-2">
                      <a
                        href={`https://app.hyperliquid.xyz/explorer/address/${t.address}`}
                        target="_blank"
                        rel="noreferrer"
                        title={t.address}
                        className="font-mono text-gf-muted hover:text-gf-accent"
                      >
                        {shortAddr(t.address)}
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <a
                        href={`https://arbiscan.io/tx/${t.txHash}`}
                        target="_blank"
                        rel="noreferrer"
                        title={t.txHash}
                        className="font-mono text-gf-muted hover:text-gf-accent"
                      >
                        {shortAddr(t.txHash)}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!transfers?.transfers || !transfers.transfers.length) && (
              <div className="px-3 py-8 text-center text-xs text-gf-muted">
                {stateNote(transfersLoading, transfersError, '暂无大额转账（≥$50k），桥上发生充值/提现时自动实时记录', 'No large transfers (≥$50k) yet — deposits/withdrawals crossing the bridge will appear automatically')}
              </div>
            )}
          </div>
          <p className="mt-2 text-[10px] text-gf-muted">
            {zh ? '数据来自 Arbitrum Hyperliquid Bridge2 合约，充值 = USDC 转入桥（进入 Hyperliquid），提现 = USDC 转出桥（离开 Hyperliquid）。' : 'Data from the Arbitrum Hyperliquid Bridge2 contract; deposit = USDC into the bridge (funds entering Hyperliquid), withdraw = USDC out of the bridge (funds leaving).'}
          </p>
        </div>
      )}

      {/* ============ OI ============ */}
      {tab === 'oi' && (
        <div className="overflow-x-auto rounded-xl border border-gf-border">
          <table className="w-full min-w-[520px] text-xs">
            <thead>
              <tr className="border-b border-gf-border bg-white/[0.03] text-left text-[11px] font-medium text-gf-muted">
                <th className="px-3 py-2">{zh ? '币种' : 'Coin'}</th>
                <th className="px-3 py-2 text-right">{zh ? '持仓 (币)' : 'OI (coins)'}</th>
                <th className="px-3 py-2 text-right">{zh ? '持仓 名义价值' : 'OI (USD)'}</th>
                <th className="px-3 py-2 text-right">{zh ? '标记价格' : 'Mark'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.03]">
              {oi?.oi.map((r, i) => (
                <tr key={i} className="hover:bg-white/[0.02]">
                  <td className="px-3 py-2 font-medium">{r.coin}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(r.openInterest)}</td>
                  <td className="px-3 py-2 text-right">{fmtUsd(r.openInterestUsd, true)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(r.markPx, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!oi?.oi || !oi.oi.length) && <div className="px-3 py-8 text-center text-xs text-gf-muted">{stateNote(oiLoading, oiError, '暂无持仓数据', 'No OI data yet')}</div>}
        </div>
      )}

      {/* ============ FUNDING ============ */}
      {tab === 'funding' && (
        <div>
          <p className="mb-2 text-[10px] leading-relaxed text-gf-muted">
            {zh
              ? '年化 = 按当前 1h 资金费率简单年化（rate × 24 × 365），仅为静态外推，不暗示未来可得收益。'
              : 'Annualized = simple annualization of the current 1h rate (rate × 24 × 365); a static extrapolation, not a promise of future returns.'}
          </p>
          <div className="overflow-x-auto rounded-xl border border-gf-border">
            <table className="w-full min-w-[560px] text-xs">
              <thead>
                <tr className="border-b border-gf-border bg-white/[0.03] text-left text-[11px] font-medium text-gf-muted">
                  <th className="px-3 py-2">{zh ? '币种' : 'Coin'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '资金费率 (1h)' : 'Funding (1h)'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '年化 (简单)' : 'Annualized*'}</th>
                  <th className="px-3 py-2 text-right">{zh ? '标记价格' : 'Mark'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.03]">
                {funding?.funding.map((r, i) => {
                  const annual = r.fundingRate * 24 * 365 * 100;
                  return (
                    <tr key={i} className="hover:bg-white/[0.02]">
                      <td className="px-3 py-2 font-medium">{r.coin}</td>
                      <td className={`px-3 py-2 text-right ${r.fundingRate >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{(r.fundingRate * 100).toFixed(4)}%</td>
                      <td className={`px-3 py-2 text-right ${annual >= 0 ? 'text-emerald-400' : 'text-red-400'}`} title={`rate×24×365 = ${(r.fundingRate * 100).toFixed(4)}% × 24 × 365`}>{annual.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right">{fmtPx(r.markPx)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(!funding?.funding || !funding.funding.length) && <div className="px-3 py-8 text-center text-xs text-gf-muted">{stateNote(fundingLoading, fundingError, '暂无资金费率数据', 'No funding data yet')}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewCard({ label, value, accent, cls, loading }: { label: string; value: string; accent?: boolean; cls?: string; loading?: boolean }) {
  return (
    <div className={`rounded-xl border border-gf-border bg-gf-card p-3 ${cls ?? ''}`}>
      <div className="text-[10px] text-gf-muted sm:text-xs">{label}</div>
      <div className={`mt-1 truncate text-sm font-bold sm:text-base ${loading ? 'animate-pulse text-gf-muted' : (accent ? 'text-gf-accent' : 'text-gf-text')}`}>
        {loading ? '—' : value}
      </div>
    </div>
  );
}

function MiniStat({ label, value, cls, title }: { label: string; value: string; cls?: string; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-[10px] text-gf-muted">{label}</div>
      <div className={`truncate text-xs ${cls ?? 'text-gf-text'}`}>{value}</div>
    </div>
  );
}

function StatBox({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="rounded-lg bg-white/[0.04] px-2.5 py-2">
      <div className="text-[10px] text-gf-muted">{label}</div>
      <div className={`text-xs font-semibold ${cls ?? 'text-gf-text'}`}>{value}</div>
    </div>
  );
}

function DirTag({ dir, side, zh, isLiq }: { dir: string; side: string; zh: boolean; isLiq?: boolean }) {
  const raw = (dir || '').toLowerCase();
  // normalize legacy raw dir values (e.g. "Buy Open", "Open Short", "Sell Close")
  let d = raw;
  if (raw.includes('liquidat')) d = raw.includes('short') ? 'liq_short' : 'liq_long';
  else if (raw.includes('open')) d = (raw.includes('short') || raw.includes('sell')) ? 'open_short' : 'open_long';
  else if (raw.includes('close')) d = (raw.includes('short') || raw.includes('sell')) ? 'close_short' : 'close_long';
  const green = 'bg-emerald-500/15 text-emerald-400';
  const red = 'bg-red-500/15 text-red-400';
  if (zh) {
    if (isLiq || d.startsWith('liq_')) {
      return <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] text-orange-400">{d === 'liq_short' ? '空单清算' : '多单清算'}</span>;
    }
    if (d === 'open_long') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${green}`}>开多</span>;
    if (d === 'open_short') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${red}`}>开空</span>;
    if (d === 'close_long') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${red}`}>平多</span>;
    if (d === 'close_short') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${green}`}>平空</span>;
    return <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-gf-muted">—</span>;
  }
  if (isLiq || d.startsWith('liq_')) {
    return <span className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] text-orange-400">{d === 'liq_short' ? 'Short LIQ' : 'Long LIQ'}</span>;
  }
  if (d === 'open_long') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${green}`}>Open Long</span>;
  if (d === 'open_short') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${red}`}>Open Short</span>;
  if (d === 'close_long') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${red}`}>Close Long</span>;
  if (d === 'close_short') return <span className={`rounded px-1.5 py-0.5 text-[10px] ${green}`}>Close Short</span>;
  return <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-gf-muted">—</span>;
}

/** D21: shared filter bar for large trades / liquidations — coin keyword, direction, min amount, time window. */
function FilterBar({
  zh, coin, setCoin, side, setSide, minUsd, setMinUsd, hours, setHours,
  amountOptions, sideOptions, thresholdNote,
}: {
  zh: boolean;
  coin: string; setCoin: (v: string) => void;
  side: string; setSide: (v: string) => void;
  minUsd: number; setMinUsd: (v: number) => void;
  hours: number; setHours: (v: number) => void;
  amountOptions: number[];
  sideOptions: Array<[string, string]>;
  thresholdNote: string;
}) {
  const inputCls = 'input !h-8 !w-auto !py-0 text-xs';
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={coin}
        onChange={(e) => setCoin(e.target.value)}
        placeholder={zh ? '币种关键词' : 'Coin keyword'}
        className={`${inputCls} !w-28`}
      />
      <select value={side} onChange={(e) => setSide(e.target.value)} className={inputCls} aria-label={zh ? '方向' : 'Direction'}>
        {sideOptions.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      <select value={minUsd} onChange={(e) => setMinUsd(Number(e.target.value))} className={inputCls} aria-label={zh ? '最低金额' : 'Min amount'}>
        {amountOptions.map((v) => (
          <option key={v} value={v}>
            {v === 0 ? (zh ? '金额不限' : 'Any amount') : `≥ $${v.toLocaleString()}`}
          </option>
        ))}
      </select>
      <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className={inputCls} aria-label={zh ? '时间范围' : 'Time window'}>
        {[[0, zh ? '时间不限' : 'Any time'], [1, zh ? '近 1 小时' : 'Last 1h'], [6, zh ? '近 6 小时' : 'Last 6h'], [24, zh ? '近 24 小时' : 'Last 24h']].map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
      <span className="text-[10px] text-gf-muted">{thresholdNote}</span>
    </div>
  );
}
